import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, FileText, Globe, ListChecks, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { LinkedInJobsTable } from "@/components/linkedin/LinkedInJobsTable";
import { LinkedInJobMatchModal } from "@/components/linkedin/LinkedInJobMatchModal";
import { linkedInApi } from "@/services/linkedin-api";
import { isJobTerminal, type LinkedInJob, type LinkedInJobStatus } from "@/types/linkedin";
import { toUserError } from "@/lib/api-error";

// Jobs já resolvidos: escondidos por padrão (a tela foca no que pede ação).
const STATUS_CONCLUIDOS: ReadonlySet<LinkedInJobStatus> = new Set([
  "applied",
  "no_changes",
  "cancelled",
]);

const STATUS_EM_PROCESSAMENTO: ReadonlySet<LinkedInJobStatus> = new Set([
  "pending",
  "uploading",
  "extracting",
  "enriching",
  "matching",
  "diffing",
]);

function formatDataLote(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
}

/** Tela de um lote: é aqui que a revisão acontece. */
export default function LinkedInLotePage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [matchJob, setMatchJob] = useState<LinkedInJob | null>(null);
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);

  const ehAvulsos = batchId === "avulsos";
  const jobsQuery = useQuery({
    queryKey: ["linkedin-jobs", "lote", batchId],
    queryFn: () =>
      linkedInApi.listJobs({ batch_id: ehAvulsos ? "null" : batchId, limit: 500 }),
    enabled: !!batchId,
    refetchInterval: (query) =>
      (query.state.data?.jobs ?? []).some((job) =>
        STATUS_EM_PROCESSAMENTO.has(job.status),
      )
        ? 2500
        : false,
  });

  const jobs = useMemo(() => jobsQuery.data?.jobs ?? [], [jobsQuery.data]);
  const stats = useMemo(() => {
    let reviewReady = 0;
    let processing = 0;
    let awaitingMatch = 0;
    let failed = 0;
    let concluidos = 0;
    let maisRecente: string | null = null;
    for (const job of jobs) {
      if (job.status === "review_ready") reviewReady++;
      else if (job.status === "awaiting_match") awaitingMatch++;
      else if (job.status === "failed") failed++;
      else if (STATUS_CONCLUIDOS.has(job.status)) concluidos++;
      else if (!isJobTerminal(job.status)) processing++;
      if (!maisRecente || job.created_at > maisRecente) maisRecente = job.created_at;
    }
    return { reviewReady, processing, awaitingMatch, failed, concluidos, maisRecente };
  }, [jobs]);

  const ehBusca = jobs.some((job) => job.source === "scraping");
  const unidade = ehBusca ? "perfil" : "arquivo";
  const jobsVisiveis = mostrarConcluidos
    ? jobs
    : jobs.filter((job) => !STATUS_CONCLUIDOS.has(job.status));
  const progressPct =
    jobs.length > 0
      ? Math.round(((jobs.length - stats.processing) / jobs.length) * 100)
      : 0;

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["linkedin-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["linkedin-batches"] });
  };

  const reprocess = useMutation({
    mutationFn: (job: LinkedInJob) => linkedInApi.reextract(job.id),
    onSuccess: () => {
      toast({
        title: "Reprocessando",
        description: "A extração foi reiniciada. Acompanhe o progresso abaixo.",
      });
      invalidar();
    },
    onError: (e: Error) => {
      toast({ title: "Erro ao reprocessar", description: toUserError(e).description, variant: "destructive" });
    },
  });

  const rescrape = useMutation({
    mutationFn: (freelancerId: string) => linkedInApi.scrapeBatch([freelancerId]),
    onSuccess: (result) => {
      toast({
        title: result.created > 0 ? "Buscando novamente" : "Nada pra buscar",
        description:
          result.created > 0
            ? "O perfil entrou em um novo processamento."
            : "Esse freelancer já tem processamento em andamento ou revisão pendente.",
      });
      invalidar();
    },
    onError: (e: Error) => {
      toast({ title: "Erro ao buscar novamente", description: toUserError(e).description, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/linkedin")}
        className="-ml-2 gap-1.5"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">
              {ehAvulsos ? "Uploads avulsos" : `Lote · ${formatDataLote(stats.maisRecente)}`}
            </h1>
            {jobs.length > 0 && (
              <Badge
                variant="outline"
                className={
                  ehBusca
                    ? "border-sky-200 bg-sky-50 text-sky-800"
                    : "bg-muted text-muted-foreground"
                }
              >
                {ehBusca ? (
                  <Globe className="mr-1 h-3 w-3" />
                ) : (
                  <FileText className="mr-1 h-3 w-3" />
                )}
                {ehBusca ? "Busca automática" : "Upload de PDFs"}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground">
            <span>
              {jobs.length} {unidade}
              {jobs.length === 1 ? "" : "s"}
            </span>
            {stats.reviewReady > 0 && (
              <span className="text-primary">{stats.reviewReady} pra revisar</span>
            )}
            {stats.processing > 0 && (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {stats.processing} processando
              </span>
            )}
            {stats.awaitingMatch > 0 && (
              <span className="text-amber-700">
                {stats.awaitingMatch} aguardando associação
              </span>
            )}
            {stats.failed > 0 && (
              <span className="text-red-700">{stats.failed} com falha</span>
            )}
            {stats.concluidos > 0 && <span>{stats.concluidos} concluídos</span>}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {stats.processing > 0 && (
            <Progress value={progressPct} className="h-1.5 w-28" />
          )}
          {!ehAvulsos && stats.reviewReady > 0 && (
            <Button
              className="gap-1.5"
              onClick={() => navigate(`/linkedin/lote/${batchId}/revisar`)}
            >
              <ListChecks className="h-4 w-4" />
              Revisar em sequência ({stats.reviewReady})
            </Button>
          )}
        </div>
      </div>

      {stats.concluidos > 0 && stats.concluidos < jobs.length && (
        <label className="flex w-fit cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Checkbox
            checked={mostrarConcluidos}
            onCheckedChange={(marcado) => setMostrarConcluidos(marcado === true)}
          />
          Mostrar concluídos ({stats.concluidos})
        </label>
      )}

      <div className="rounded-lg border bg-card">
        {jobsQuery.isPending ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : jobsVisiveis.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {jobs.length === 0 ? "Lote não encontrado." : "Nada pendente neste lote."}
          </div>
        ) : (
          <LinkedInJobsTable
            jobs={jobsVisiveis}
            onAssociate={(job) => setMatchJob(job)}
            onReview={(job) => navigate(`/linkedin/jobs/${job.id}/revisar`)}
            onReprocess={(job) => reprocess.mutate(job)}
            onRescrape={(job) => job.freelancer_id && rescrape.mutate(job.freelancer_id)}
          />
        )}
      </div>

      <LinkedInJobMatchModal job={matchJob} onClose={() => setMatchJob(null)} />
    </div>
  );
}
